import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Inbox, MessageCircle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { type ChatActionResponse, type ChatMessage } from '@/lib/api';

export function ChatMessageBubble({ message }: { message: ChatMessage }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-subtle text-text-primary max-w-[85%] rounded-2xl rounded-br-md px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap">
          {message.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2">
      <div className="bg-accent/10 text-accent mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
        <Sparkles className="h-3 w-3" />
      </div>
      <div className="text-text-primary prose prose-sm dark:prose-invert max-w-[85%] text-sm leading-relaxed">
        <ReactMarkdown>{message.content}</ReactMarkdown>
      </div>
    </div>
  );
}

/**
 * V153 — action chips under the assistant reply. Currently the only action
 * type is `draft_posts_created`; every action deep-links to the Review inbox
 * so the user can approve what the assistant just drafted. The aligned
 * left spacer matches the assistant bubble's avatar gutter.
 */
export function ChatActions({
  actions,
  onNavigate,
}: {
  actions: ChatActionResponse[];
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const go = () => {
    navigate('/dashboard/review');
    onNavigate();
  };
  return (
    <div className="flex items-start gap-2">
      <div className="h-6 w-6 flex-shrink-0" />
      <div className="flex max-w-[85%] flex-col gap-1.5">
        {actions.map((action, i) => (
          <button
            key={i}
            type="button"
            onClick={go}
            className="bg-accent/15 text-accent hover:border-accent border-border inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-left text-xs font-medium transition-colors"
          >
            <Inbox className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{action.summary}</span>
            <span className="text-text-tertiary ml-1 whitespace-nowrap">
              {t('chatbot.openReview')} →
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ChatTypingIndicator() {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-2">
      <div className="bg-accent/10 text-accent mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
        <Sparkles className="h-3 w-3" />
      </div>
      <div className="text-text-tertiary inline-flex items-center gap-1 text-xs">
        <span>{t('chatbot.thinking')}</span>
        <span className="inline-flex gap-0.5">
          <span className="h-1 w-1 animate-pulse rounded-full bg-current" />
          <span
            className="h-1 w-1 animate-pulse rounded-full bg-current"
            style={{ animationDelay: '0.15s' }}
          />
          <span
            className="h-1 w-1 animate-pulse rounded-full bg-current"
            style={{ animationDelay: '0.3s' }}
          />
        </span>
      </div>
    </div>
  );
}

export function ChatWelcome({
  promptSuggestions,
  onPromptClick,
}: {
  promptSuggestions: string[];
  onPromptClick: (p: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-3 px-1">
      <div className="bg-accent/10 text-accent flex h-9 w-9 items-center justify-center rounded-full">
        <Sparkles className="h-4 w-4" />
      </div>
      <div>
        <p className="text-text-primary text-sm font-semibold">{t('chatbot.welcomeTitle')}</p>
        <p className="text-text-secondary mt-1 text-sm leading-relaxed">
          {t('chatbot.welcomeBody')}
        </p>
      </div>
      <div className="mt-2 flex w-full flex-col gap-1.5">
        {promptSuggestions.map((p, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onPromptClick(p)}
            className="border-border hover:border-accent hover:text-accent text-text-secondary rounded-lg border px-3 py-2 text-left text-xs transition-colors"
          >
            <MessageCircle className="mr-1.5 inline h-3 w-3" />
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
